'use client'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { extractErrorMessage, getNameFromEmail, hasResponseStatus, hasStatus } from '@/utils'
import { authClient } from '@/clients/better-auth'
import { AppUrls } from '@/enums'
import { LinkWrapper } from '@/components/common/LinkWrapper'
import { CenteredContent } from '@/components/app/CenteredContent'
import { AppButton } from '@/components/ui/AppButton'
import { AppInput } from '@/components/ui/AppInput'

type FormData = {
    name: string
    email: string
    password: string
    verifyPassword: string
    image?: string
}

export function SignUpForm() {
    const form = useForm<FormData>({
        defaultValues: { name: '', email: '', password: '', verifyPassword: '', image: '' },
    })

    const mutation = useMutation({
        mutationFn: async (values: FormData) => {
            if (!values.email.includes('@')) throw new Error('Please enter a valid email address')
            if (!values.password || values.password.length < 8) throw new Error('Password must be at least 8 characters')
            if (values.password !== values.verifyPassword) throw new Error('Passwords do not match')

            // https://www.better-auth.com/docs/basic-usage#sign-up
            return authClient.signUp.email({
                email: values.email,
                password: values.password,
                image: values.image,
                name: getNameFromEmail(values.email) ?? values.email,
                callbackURL: AppUrls.EMAIL_VERIFIED,
            })
        },
        onError: (error: unknown) => {
            if ((hasStatus(error) && error.status === 403) || (hasResponseStatus(error) && error.response.status === 403)) {
                window.location.href = AppUrls.VERIFY_EMAIL
                return
            }
            toast.error(extractErrorMessage((error as Error).message) || 'Signup failed')
        },
        onSuccess: () => {
            toast.success('Account created successfully')
            toast.success('Please check your email to verify your account')
            window.location.href = AppUrls.VERIFY_EMAIL
        },
    })

    function onSubmit(values: FormData) {
        mutation.mutate(values)
    }

    return (
        <CenteredContent>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex w-full flex-col gap-4">
                <h2 className="text-2xl font-semibold">Create a new account</h2>
                <div>
                    <label>
                        <p className="text-sm">Email</p>
                        <AppInput
                            type="email"
                            {...form.register('email', {
                                required: 'Email is required',
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: 'Please enter a valid email address',
                                },
                            })}
                            placeholder="Your email"
                        />
                    </label>
                    {form.formState.errors.email && <div style={{ color: 'red' }}>{form.formState.errors.email.message}</div>}
                </div>
                <div>
                    <label>
                        <p className="text-sm">Password</p>
                        <AppInput
                            type="password"
                            {...form.register('password', {
                                required: 'Password is required',
                                minLength: {
                                    value: 8,
                                    message: 'Password must be at least 8 characters',
                                },
                            })}
                            placeholder="Your password"
                        />
                    </label>
                    {form.formState.errors.password && <div style={{ color: 'red' }}>{form.formState.errors.password.message}</div>}
                </div>
                <div>
                    <label>
                        <p className="text-sm">Verify password</p>
                        <AppInput
                            type="password"
                            {...form.register('verifyPassword', {
                                required: 'Please verify your password',
                                validate: (value) => value === form.getValues('password') || 'Passwords do not match',
                            })}
                            placeholder="Verify your password"
                        />
                    </label>
                    {form.formState.errors.verifyPassword && <div style={{ color: 'red' }}>{form.formState.errors.verifyPassword.message}</div>}
                </div>
                <AppButton type="submit" loading={mutation.isPending} loadingText="Signing up..." disabled={!form.formState.isValid}>
                    Sign up
                </AppButton>
                <LinkWrapper href={AppUrls.SIGN_IN} className="mx-auto hover:underline">
                    <p>Already have an account? Sign in</p>
                </LinkWrapper>
            </form>
        </CenteredContent>
    )
}
